import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  MapPin,
  Clock,
  DollarSign,
  Home as HomeIcon,
  ShieldCheck,
  Wrench,
  Headset,
  GraduationCap,
  FileText,
  Phone,
  ClipboardCheck,
  Truck,
  ChevronDown,
  Check,
  Quote,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { company } from "@/content/site";
import { APPLY_TYPE_LABEL, jobIcon, type Job } from "@/content/jobs";
import { listPublishedJobs } from "@/features/jobs/repo";

const perks = [
  { icon: DollarSign, title: "Tanker-premium pay", body: "Liquid bulk is our main work and pays more than general freight - and we pay on time, every time." },
  { icon: Wrench, title: "Modern equipment", body: "2023 Freightliner Cascadia and Kenworth T680 tractors (or newer) with automatic transmissions, paired with maintained stainless tankers." },
  { icon: HomeIcon, title: "Real home time", body: "Regional and OTR options - many OTR runs are about 3 weeks out with 3-4 days off, so you can pick the schedule that fits your life." },
  { icon: ShieldCheck, title: "Safety-first culture", body: "Ongoing training, clean equipment, and a team that never cuts corners." },
  { icon: Headset, title: "Dispatch that answers", body: "Real people who pick up the phone and have your back on the road." },
  { icon: GraduationCap, title: "Paid orientation", body: "Get road-ready and earning quickly with hands-on, paid orientation." },
];

const eligibility = [
  {
    title: "Company Drivers",
    badge: "Company driver",
    to: "/apply?type=driver",
    items: [
      "Valid CDL-A",
      "2+ years recent tractor-trailer experience",
      "Tanker endorsement (or willing to obtain)",
      "HazMat endorsement a plus",
      "Clean MVR, DOT-qualifiable",
    ],
  },
  {
    title: "Owner-Operators",
    badge: "Owner-operator",
    to: "/apply?type=carrier",
    items: [
      "Your own well-maintained tractor",
      "CDL-A with tanker endorsement",
      "Authority & insurance (or run under ours)",
      "HazMat endorsement a plus",
      "Clean MVR, DOT-qualifiable",
    ],
  },
];

const steps = [
  { icon: FileText, title: "Apply online", body: "A short application - a few minutes is all it takes." },
  { icon: Phone, title: "Talk to recruiting", body: "We call to learn about you and answer your questions." },
  { icon: ClipboardCheck, title: "Orientation", body: "Paid, hands-on orientation gets you road-ready." },
  { icon: Truck, title: "Hit the road", body: "Get dispatched and start earning with a team behind you." },
];

const testimonials = [
  {
    quote:
      "Working as a truck driver for Bull and Bear is truly an exceptional experience. The commitment to safety, the well-maintained fleet, and the supportive environment make every day a pleasure. They show genuine respect for their drivers and always make sure we're well taken care of. I wholeheartedly recommend Bull and Bear to any driver looking for a professional, reliable, and rewarding company.",
    name: "Igor Dlhy",
    role: "Company driver",
  },
  {
    quote:
      "My name is Tom, I'm an owner-operator with 50 years of experience. I've been with Bull and Bear going on 6 years - they're by far the best company I've worked for. They treat you like family, with an open door to everyone in the office. If you're looking for a home, this is the place.",
    name: "Thomas Dyer",
    role: "Owner-operator",
  },
];

const faqs = [
  {
    q: "How much experience do I need?",
    a: "We generally look for at least two years of recent tractor-trailer experience, but we review every application individually - if you're close, reach out.",
  },
  {
    q: "What kind of home time can I expect?",
    a: "We run both regional and OTR lanes, so home time depends on the route. Many OTR runs are about 3 weeks out with 3-4 days off. Tell recruiting what you need and we'll match you to the right board.",
  },
  {
    q: "What equipment will I drive?",
    a: "Most openings are for 2023 Freightliner Cascadia and Kenworth T680 tractors (or newer) with automatic transmissions, paired with well-maintained stainless steel tankers. From time to time we also have dry van and reefer work, but tanker freight is the core of the company.",
  },
  {
    q: "Do I need a tanker or HazMat endorsement?",
    a: "A tanker endorsement is required for most routes and HazMat is a strong plus. Don't have them yet? We can talk through getting endorsed.",
  },
  {
    q: "Where do you hire?",
    a: "We're Midwest-based and run all 48 contiguous states plus cross-border lanes into Canada. Hiring areas vary by lane - check current openings or ask recruiting.",
  },
  {
    q: "How does pay work?",
    a: "Pay varies by route and experience and is listed on each open position. However it's structured, we pay on time, every time.",
  },
];

export default function Careers() {
  const [jobs, setJobs] = useState<Job[]>([]);

  useEffect(() => {
    listPublishedJobs()
      .then(setJobs)
      .catch(() => setJobs([]));
  }, []);

  return (
    <>
      <Seo
        title={`Careers - Tanker Driving Jobs & Owner-Operators | ${company.name}`}
        description="Tanker driving jobs and owner-operator opportunities at Bull and Bear Co. OTR, regional, and HazMat routes - premium pay, modern equipment, and dispatch that answers."
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-brand-900 py-20 text-white lg:py-28">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: "repeating-linear-gradient(135deg, #fff 0 2px, transparent 2px 26px)" }}
        />
        <div className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-accent-500/15 blur-3xl" />
        <div className="relative mx-auto max-w-[1200px] px-6">
          <Reveal>
            <Badge className="mb-6 border-accent-500/30 bg-accent-500/15 text-accent-300 hover:bg-accent-500/20">
              We're hiring
            </Badge>
            <h1 className="mb-5 max-w-3xl text-[clamp(2.25rem,5vw,3.75rem)] font-bold leading-[1.05] tracking-tight">
              Drive with a tanker carrier that{" "}
              <span className="text-accent-400">treats you like a pro.</span>
            </h1>
            <p className="mb-8 max-w-2xl text-lg text-brand-200">
              {company.name} is hiring company drivers and owner-operators to haul
              liquid bulk across the U.S. and into Canada. Most work is tanker, with
              some dry van and reefer opportunities depending on lane, season, and
              customer need.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href="#openings" className={cn(buttonVariants({ variant: "accent", size: "lg" }), "w-full justify-center sm:w-auto")}>
                View open positions
              </a>
              <Link
                to="/apply"
                className={cn(
                  buttonVariants({ variant: "outline", size: "lg" }),
                  "w-full justify-center border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white sm:w-auto",
                )}
              >
                Apply now
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-brand-300">
              <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4 text-accent-400" /> 48-state lanes</span>
              <span className="inline-flex items-center gap-1.5"><Wrench className="h-4 w-4 text-accent-400" /> Modern stainless equipment</span>
              <span className="inline-flex items-center gap-1.5"><Headset className="h-4 w-4 text-accent-400" /> Dispatch that answers</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY DRIVE FOR US */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-[1200px] px-6">
          <Reveal>
            <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent-500">Why Bull & Bear</div>
            <h2 className="mb-12 max-w-2xl text-3xl lg:text-4xl">More than a paycheck.</h2>
          </Reveal>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {perks.map((b, i) => (
              <Reveal key={b.title} delay={i * 70} className="h-full">
                <div className="h-full rounded-lg border border-brand-100 p-6">
                  <b.icon className="mb-3 h-7 w-7 text-accent-500" />
                  <h3 className="mb-1 text-lg font-semibold text-brand-900">{b.title}</h3>
                  <p className="text-sm text-brand-600">{b.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHO WE'RE HIRING (eligibility at a glance) */}
      <section className="bg-brand-50 py-16 lg:py-20">
        <div className="mx-auto max-w-[1200px] px-6">
          <Reveal>
            <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent-500">Who we're hiring</div>
            <h2 className="mb-12 max-w-2xl text-3xl lg:text-4xl">Two ways to roll with us.</h2>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-2">
            {eligibility.map((e, i) => (
              <Reveal key={e.title} delay={i * 100} className="h-full">
                <div className="flex h-full flex-col rounded-xl border border-brand-100 bg-white p-7">
                  <h3 className="mb-1 text-xl font-bold text-brand-900">{e.title}</h3>
                  <div className="mb-5 text-sm text-brand-400">What you'll need to qualify</div>
                  <ul className="mb-7 space-y-2.5">
                    {e.items.map((it) => (
                      <li key={it} className="flex items-start gap-2.5 text-brand-700">
                        <Check className="mt-0.5 h-5 w-5 shrink-0 text-accent-500" /> {it}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={e.to}
                    className={cn(buttonVariants({ variant: "accent" }), "mt-auto self-start")}
                  >
                    Apply as {e.title.toLowerCase().replace(/s$/, "")} <ArrowRight className="ml-1 h-4 w-4" />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-[1200px] px-6">
          <Reveal>
            <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent-500">How it works</div>
            <h2 className="mb-12 max-w-2xl text-3xl lg:text-4xl">From application to first load.</h2>
          </Reveal>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((s, i) => (
              <Reveal key={s.title} delay={i * 80} className="h-full">
                <div className="relative h-full rounded-lg border border-brand-100 p-6">
                  <div className="mb-4 flex items-center justify-between">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-brand-900 text-white">
                      <s.icon className="h-5 w-5" />
                    </span>
                    <span className="text-4xl font-bold text-brand-100">{i + 1}</span>
                  </div>
                  <h3 className="mb-1 text-lg font-semibold text-brand-900">{s.title}</h3>
                  <p className="text-sm text-brand-600">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT DRIVERS SAY */}
      <section className="bg-brand-900 py-16 text-white lg:py-20">
        <div className="mx-auto max-w-[1200px] px-6">
          <Reveal>
            <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent-400">Driver voices</div>
            <h2 className="mb-12 max-w-2xl text-3xl text-white lg:text-4xl">What our drivers say.</h2>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-2">
            {testimonials.map((t, i) => (
              <Reveal key={t.name} delay={i * 100} className="h-full">
                <figure className="flex h-full flex-col rounded-xl border border-white/10 bg-white/5 p-7">
                  <Quote className="mb-4 h-8 w-8 shrink-0 text-accent-400" />
                  <blockquote className="mb-5 flex-1 text-brand-100">{t.quote}</blockquote>
                  <figcaption className="text-sm">
                    <div className="font-semibold text-white">{t.name}</div>
                    <div className="text-brand-300">{t.role}</div>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* OPENINGS (CMS-driven) */}
      <section id="openings" className="scroll-mt-24 bg-brand-50 py-16 lg:py-20">
        <div className="mx-auto max-w-[1200px] px-6">
          <Reveal>
            <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent-500">Open positions</div>
            <h2 className="mb-12 max-w-2xl text-3xl lg:text-4xl">Find your seat.</h2>
          </Reveal>
          {jobs.length === 0 && (
            <p className="rounded-xl border border-dashed border-brand-200 bg-white p-8 text-center text-brand-500">
              No open positions are posted right now - but we're always glad to hear
              from good drivers.{" "}
              <Link to="/apply" className="font-semibold text-accent-600 hover:underline">
                Send us a general application.
              </Link>
            </p>
          )}
          <div className="grid gap-6">
            {jobs.map((job, i) => {
              const Icon = jobIcon(job.applyType);
              return (
              <Reveal key={job.slug} delay={i * 60}>
                <div className="grid gap-6 rounded-xl border border-brand-100 bg-white p-6 shadow-sm transition-shadow hover:shadow-md lg:grid-cols-[1fr_auto] lg:p-8">
                  <div>
                    <div className="mb-3 flex flex-wrap items-center gap-2">
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-accent-500/10 text-accent-600">
                        <Icon className="h-5 w-5" />
                      </span>
                      <h3 className="text-xl font-bold text-brand-900">{job.title}</h3>
                      <Badge
                        className={cn(
                          "ml-1",
                          job.applyType === "carrier"
                            ? "bg-brand-900 text-white hover:bg-brand-900"
                            : job.applyType === "office"
                              ? "bg-brand-100 text-brand-700 hover:bg-brand-100"
                              : "bg-accent-500/15 text-accent-700 hover:bg-accent-500/15",
                        )}
                      >
                        {APPLY_TYPE_LABEL[job.applyType]}
                      </Badge>
                    </div>

                    <p className="mb-4 max-w-2xl text-brand-600">{job.summary}</p>

                    <div className="mb-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-brand-700">
                      {job.employment && (
                        <span className="inline-flex items-center gap-1.5">
                          <Clock className="h-4 w-4 text-brand-400" /> {job.employment}
                        </span>
                      )}
                      {job.location && (
                        <span className="inline-flex items-center gap-1.5">
                          <MapPin className="h-4 w-4 text-brand-400" /> {job.location}
                        </span>
                      )}
                      {job.homeTime && (
                        <span className="inline-flex items-center gap-1.5">
                          <HomeIcon className="h-4 w-4 text-brand-400" /> {job.homeTime}
                        </span>
                      )}
                      {job.pay && (
                        <span className="inline-flex items-center gap-1.5 font-semibold text-brand-900">
                          <DollarSign className="h-4 w-4 text-accent-500" /> {job.pay}
                        </span>
                      )}
                    </div>

                    <div className="grid gap-5 sm:grid-cols-2">
                      <div>
                        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-brand-400">Requirements</div>
                        <ul className="space-y-1.5 text-sm text-brand-600">
                          {job.requirements.map((r) => (
                            <li key={r} className="flex gap-2">
                              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-500" />
                              {r}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-brand-400">What you get</div>
                        <ul className="space-y-1.5 text-sm text-brand-600">
                          {job.highlights.map((h) => (
                            <li key={h} className="flex gap-2">
                              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-300" />
                              {h}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start lg:items-center">
                    <Link
                      to={`/apply?type=${job.applyType}&job=${job.slug}`}
                      className={cn(buttonVariants({ variant: "accent", size: "lg" }), "w-full lg:w-auto")}
                    >
                      Apply <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </Reveal>
              );
            })}
          </div>

          {jobs.length > 0 && (
            <Reveal className="mt-10 rounded-xl border border-dashed border-brand-200 bg-white p-6 text-center">
              <p className="text-brand-600">
                Don't see the right fit?{" "}
                <Link to="/apply" className="font-semibold text-accent-600 hover:underline">
                  Send us a general application
                </Link>{" "}
                - we're always looking for good drivers.
              </p>
            </Reveal>
          )}
        </div>
      </section>

      {/* DRIVER FAQ */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-[820px] px-6">
          <Reveal>
            <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent-500">Driver FAQ</div>
            <h2 className="mb-10 text-3xl lg:text-4xl">Good questions, straight answers.</h2>
          </Reveal>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <Reveal key={f.q} delay={i * 50}>
                <details className="group rounded-lg border border-brand-100 bg-white open:shadow-sm">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-5 font-semibold text-brand-900 [&::-webkit-details-marker]:hidden">
                    {f.q}
                    <ChevronDown className="h-5 w-5 shrink-0 text-accent-500 transition-transform group-open:rotate-180" />
                  </summary>
                  <div className="px-5 pb-5 text-brand-600">{f.a}</div>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-brand-900 py-16 text-white">
        <Reveal className="mx-auto flex max-w-[1200px] flex-col items-center justify-between gap-6 px-6 md:flex-row">
          <div>
            <h2 className="text-3xl lg:text-4xl">Ready to roll?</h2>
            <p className="mt-2 max-w-xl text-brand-300">
              Apply in a few minutes, or call our recruiting team and we'll take it
              from there.
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap gap-3">
            <Link to="/apply" className={cn(buttonVariants({ variant: "accent", size: "lg" }), "w-full justify-center sm:w-auto")}>
              Apply now <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
            <a
              href={`tel:${company.phone.replace(/[^+\d]/g, "")}`}
              className={cn(
                buttonVariants({ variant: "outline", size: "lg" }),
                "w-full justify-center border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white sm:w-auto",
              )}
            >
              <Phone className="mr-1 h-4 w-4" /> {company.phone}
            </a>
          </div>
        </Reveal>
      </section>
    </>
  );
}
